import { useState } from "react";
import { useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import {
  Button,
  Empty,
  ErrorBlock,
  Input,
  OrderStatus,
  Price,
  Spinner,
} from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { orgFetch } from "../../api/org-client.ts";
import { useIdentities, useMe } from "../../api/queries.ts";
import {
  PAY_METHODS,
  TABS,
  maskPhone,
  type FulfillmentPolicy,
  type PendingRefund,
  type SellerOrder,
} from "../seller-orders.tsx";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 客户订单
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这一页是拿来对账的，钱要排在同一条竖线上。**
 *    每单有三个数：客户付我多少、我该付上级多少、我赚多少。
 *    手机上它们分散在三行文字里，一单一单读；网页上收进右边固定宽度的一栏，
 *    十几单排下来**一列扫下去就能对账** —— 而这个平台钱不经平台，
 *    对账全靠商家自己。
 *
 * 🔴 **待处理退款排在最上面，做成表。** 退款是有时效的（客户在等），
 *    混在订单流里会被漏掉。
 */

const orgGet = <T,>(path: string, orgId: number) => orgFetch<T>(path, orgId);
const orgPost = <T,>(path: string, orgId: number, body: unknown) =>
  orgFetch<T>(path, orgId, { method: "POST", body: JSON.stringify(body) });

/**
 * 确认收到线下货款。
 *
 * 🔴 **这个商城不走线上支付**：线上只建订单，钱走别的渠道
 *    （转账 / 微信 / 现金 / 对公）。收款方在这里点一下，货才发得出去。
 *
 * ⚠️ **收款方式必须留痕**：线下收款没有第三方流水做旁证，
 *    这条记录就是唯一的凭据，纠纷时全靠它。
 */
function ConfirmPayBox({
  orderId,
  orgId,
  amount,
  onDone,
}: {
  orderId: number;
  orgId: number;
  amount: number;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<string>(PAY_METHODS[0]);
  const [note, setNote] = useState("");

  const confirm = useMutation({
    mutationFn: () => orgPost(`/orders/${orderId}/confirm-payment`, orgId, { method, note }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  if (!open)
    return (
      <Button size="sm" className="w-full" onClick={() => setOpen(true)}>
        确认已收款
      </Button>
    );

  return (
    <div className="space-y-2.5">
      {/*
        🔴 金额是**服务端算的**，这里只显示不让改 ——
           让前端传金额等于点一下就能把「已收 ¥5824」记成「已收 ¥1」。
      */}
      <p className="text-[13px] leading-relaxed text-muted-foreground">
        确认收到 <Price cents={amount} className="text-brand-600" />？确认后这单就可以发货了。
      </p>
      <div className="flex flex-wrap gap-1.5">
        {PAY_METHODS.map((m) => (
          <button
            key={m}
            onClick={() => setMethod(m)}
            className={cn(
              "rounded-lg border px-2.5 py-1 text-[13px] transition-colors",
              method === m
                ? "border-brand-600 bg-brand-50 text-brand-700"
                : "border-border text-muted-foreground hover:border-brand-300",
            )}
          >
            {m}
          </button>
        ))}
      </div>
      <Input
        placeholder="备注（流水号、经手人，选填）"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex gap-2">
        <Button size="sm" loading={confirm.isPending} onClick={() => confirm.mutate()}>
          确认收款
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          取消
        </Button>
      </div>
      {confirm.error && <ErrorBlock error={confirm.error} />}
    </div>
  );
}

/** 发货表单。⚠️ 承运商和单号都必填 —— 少一个买家就查不到物流。 */
function ShipBox({
  orderId,
  orgId,
  onDone,
}: {
  orderId: number;
  orgId: number;
  onDone: () => void;
}) {
  const [carrier, setCarrier] = useState("");
  const [trackingNo, setTrackingNo] = useState("");

  const ship = useMutation({
    mutationFn: () => orgPost(`/supply-orders/${orderId}/ship`, orgId, { carrier, trackingNo }),
    onSuccess: onDone,
  });

  return (
    <div className="space-y-2">
      <Input placeholder="承运商" value={carrier} onChange={(e) => setCarrier(e.target.value)} />
      <Input
        placeholder="快递单号"
        value={trackingNo}
        onChange={(e) => setTrackingNo(e.target.value)}
      />
      <Button
        size="sm"
        className="w-full"
        loading={ship.isPending}
        disabled={!carrier.trim() || !trackingNo.trim()}
        onClick={() => ship.mutate()}
      >
        发货
      </Button>
      {ship.error && <ErrorBlock error={ship.error} />}
    </div>
  );
}

/**
 * 我店里的待审退款。
 *
 * 🔴 `afterSaleBy=seller` 时售后归卖家处理，但以前**只有平台后台有入口**。
 * ⚠️ 服务端只出他自己店里的单：别人的退款申请里有订单号、金额和买家诉求。
 */
function SellerRefunds({ orgId }: { orgId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["seller-refunds", orgId],
    queryFn: () =>
      orgGet<{ items: PendingRefund[]; policy: FulfillmentPolicy }>("/refunds", orgId),
  });

  const done = () => {
    qc.invalidateQueries({ queryKey: ["seller-refunds", orgId] });
    qc.invalidateQueries({ queryKey: ["seller-orders"] });
  };
  const approve = useMutation({
    mutationFn: (id: number) => orgPost(`/refunds/${id}/approve`, orgId, {}),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: (v: { id: number; reason: string }) =>
      orgPost(`/refunds/${v.id}/reject`, orgId, { reason: v.reason }),
    onSuccess: done,
  });

  /* ⚠️ 平台统一处理售后时这一块整个不出现，别给一个点不动的入口。 */
  if (q.data?.policy.afterSaleBy === "platform")
    return (
      <p className="mb-5 rounded-xl border border-border bg-card px-5 py-3.5 text-[13px] text-muted-foreground">
        当前<strong className="text-foreground">售后由平台统一处理</strong>
        ，客户的退款申请会直接进平台客服队列。
      </p>
    );

  if (q.isPending || (q.data?.items.length ?? 0) === 0) return null;

  return (
    <section className="mb-5 overflow-hidden rounded-xl border border-amber-200 bg-card">
      <div className="border-b border-amber-200 bg-amber-50 px-5 py-3">
        <p className="text-[15px] font-medium text-amber-900">
          待处理退款（{q.data!.items.length}）
        </p>
        {/* 🔴 批了钱就从他的分账里扣回来，这句话要说在按钮旁边。 */}
        <p className="mt-0.5 text-[13px] text-amber-800">
          同意之后钱立刻退给买家，<strong>并从你这一单的分成里扣回</strong>。
        </p>
      </div>

      <div className="grid grid-cols-[160px_150px_minmax(0,1fr)_200px] gap-4 border-b border-border px-5 py-2 text-[13px] text-muted-foreground">
        <span>订单号</span>
        <span>申请退款</span>
        <span>理由</span>
        <span />
      </div>

      {q.data!.items.map((r) => (
        <div
          key={r.id}
          className="grid grid-cols-[160px_150px_minmax(0,1fr)_200px] items-center gap-4 border-b border-border px-5 py-3 last:border-b-0"
        >
          <span className="font-mono text-[13px] text-muted-foreground">{r.orderNo}</span>
          <span className="text-[14px]">
            <Price cents={r.amount} className="text-red-600" />
            <span className="mt-0.5 block text-[12px] text-muted-foreground">
              实付 <Price cents={r.paidAmount} />
              {r.alreadyRefunded > 0 && (
                <>
                  {" · "}已退 <Price cents={r.alreadyRefunded} />
                </>
              )}
            </span>
          </span>
          <span className="min-w-0 text-[13px] text-muted-foreground">
            {r.reason || "（未填）"}
          </span>
          <span className="flex justify-end gap-2">
            <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate(r.id)}>
              同意
            </Button>
            <Button
              size="sm"
              variant="outline"
              loading={reject.isPending}
              onClick={() => {
                /* ⚠️ 驳回必须写理由 —— 买家要知道为什么被拒，否则只会反复申请。 */
                const reason = window.prompt("驳回理由（会展示给买家）");
                if (reason && reason.trim()) reject.mutate({ id: r.id, reason: reason.trim() });
              }}
            >
              驳回
            </Button>
          </span>
        </div>
      ))}
      {(approve.error || reject.error) && (
        <div className="px-5 py-3">
          <ErrorBlock error={approve.error ?? reject.error!} />
        </div>
      )}
    </section>
  );
}

export function DesktopSellerOrders() {
  const search = useSearch({ from: "/seller-orders" }) as { orgId?: number };
  const { data: me, isPending: mePending } = useMe();
  const { data: identities } = useIdentities(Boolean(me));

  const orgs = (identities?.orgs ?? []).filter((o) => o.status === "active");
  const [picked, setPicked] = useState<number | null>(search.orgId ?? null);
  const orgId = picked ?? orgs[0]?.id ?? null;

  const [payment, setPayment] = useState("");
  const [keyword, setKeyword] = useState("");
  /**
   * 🔴 **「从我这出货的」这一档是给上级看的。**
   *    王师傅卖出一件瓷砖，货是从经销商那里进的 —— 经销商得知道
   *    "我这里出了一件货、要给王师傅备货"。
   */
  const [scope, setScope] = useState<"mine" | "chain">("mine");

  /** 该不该出现发货入口，由服务端的 `shipBy` 说了算。 */
  const policy = useQuery({
    queryKey: ["fulfillment-policy", orgId],
    queryFn: () => orgGet<FulfillmentPolicy>("/fulfillment-policy", orgId!),
    enabled: Boolean(orgId),
  });

  const list = useQuery({
    queryKey: ["seller-orders", orgId, payment, keyword, scope],
    queryFn: () => {
      const qs = new URLSearchParams({ limit: "30" });
      if (payment) qs.set("payment", payment);
      if (keyword) qs.set("keyword", keyword);
      if (scope === "chain") qs.set("scope", "chain");
      return orgGet<{ items: SellerOrder[]; nextCursor: string | null }>(`/orders?${qs}`, orgId!);
    },
    enabled: Boolean(orgId),
  });

  if (mePending)
    return (
      <AccountShell title="客户订单">
        <Spinner compact />
      </AccountShell>
    );

  if (!me || orgs.length === 0)
    return (
      <AccountShell title="客户订单">
        <Empty title="你还不是任何商家的成员" hint="这是商家功能，联系上级商家或平台开通" compact />
      </AccountShell>
    );

  return (
    <AccountShell title="客户订单">
      {orgs.length > 1 && (
        <div className="mb-4 flex gap-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              onClick={() => setPicked(o.id)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                o.id === orgId
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}

      {/* ── 我卖的 / 从我这出货的 ── */}
      <div className="mb-4 flex border-b border-border">
        {(
          [
            { k: "mine", label: "我卖的" },
            { k: "chain", label: "从我这出货的" },
          ] as const
        ).map((t) => (
          <button
            key={t.k}
            onClick={() => setScope(t.k)}
            className={cn(
              "relative px-4 py-2.5 text-[15px] transition-colors",
              scope === t.k
                ? "font-medium text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {scope === t.k && (
              <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-600" />
            )}
          </button>
        ))}
      </div>

      {scope === "chain" && (
        <p className="mb-4 rounded-xl bg-brand-50 px-5 py-3.5 text-[13px] leading-relaxed text-brand-700">
          这里是<strong>经过你这一级</strong>的全部订单，含你下级卖出去的。
          <strong>本单收款</strong>是你这一单收到多少（下级付给你的），
          <strong>该付上级</strong>是你要结给上家的货款 —— 钱不经平台，两边都是你自己线下收付。
          商品和物流全链同步；下游最终卖了多少钱是他的经营信息，这里不显示。
        </p>
      )}

      <div className="mb-5 flex items-center gap-4">
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setPayment(t.key)}
              className={cn(
                "rounded-lg px-3.5 py-1.5 text-[14px] transition-colors",
                payment === t.key
                  ? "bg-brand-600 text-white"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-[300px]">
          <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="搜订单号或客户手机号"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {orgId && <SellerRefunds orgId={orgId} />}

      {list.isPending ? (
        <Spinner compact />
      ) : list.error ? (
        <ErrorBlock error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        <Empty title="还没有订单" hint="把专属链接发给客户，他们下的单会出现在这里" compact />
      ) : (
        <div className="space-y-4">
          {list.data.items.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex items-center gap-4 border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px]">
                <span className="font-mono text-muted-foreground">{o.orderNo}</span>
                <span className="text-muted-foreground">
                  {new Date(o.createdAt).toLocaleString("zh-CN")}
                </span>
                {/* 商家要能联系上客户处理售后，但手机号中间打码。 */}
                <span className="text-muted-foreground">
                  {o.shipName} {maskPhone(o.shipPhone)}
                </span>
                <span className="ml-auto">
                  <OrderStatus payment={o.payment as never} fulfillment={o.fulfillment as never} />
                </span>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_260px]">
                {/* ── 左：卖了什么 ── */}
                <div className="divide-y divide-border">
                  {o.items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-baseline justify-between gap-4 px-5 py-3 text-[14px]"
                    >
                      <span className="min-w-0">
                        {it.titleSnapshot}
                        {/* ⚠️ 快照可能是空对象甚至 null（老数据），别直接 Object.values。 */}
                        <span className="ml-2 text-[12px] text-muted-foreground">
                          {Object.values(it.specsSnapshot ?? {}).join(" · ")}
                        </span>
                      </span>
                      <span className="shrink-0 text-muted-foreground">
                        {/* ⚠️ 链视角下服务端不给单价 —— 那是下游的售价。 */}
                        {it.unitPrice != null ? <Price cents={it.unitPrice} /> : "—"} ×{" "}
                        {it.quantity}
                      </span>
                    </div>
                  ))}

                  {/*
                    物流。🔴 全链同步 —— 上游也要看得到，
                       客户问到他那儿时才答得上来「货到哪了」。
                  */}
                  {o.trackingNo && (
                    <div className="flex items-center gap-3 px-5 py-2.5 text-[13px]">
                      <span className="text-muted-foreground">物流</span>
                      <span className="font-mono">
                        {o.shippingCarrier} {o.trackingNo}
                      </span>
                    </div>
                  )}
                </div>

                {/* ── 右：钱与动作（对账就看这一列） ── */}
                <div className="space-y-3 border-l border-border p-5">
                  <div className="flex items-baseline justify-between text-[14px]">
                    {/*
                      ⚠️ 两个视角显示的是**不同的数**：
                         「我卖的」是终端成交价；「从我这出货的」是按我这一级算的钱。
                         标签也要跟着变 —— 都叫"成交"的话，上级会以为那是终端价。
                    */}
                    <span className="text-muted-foreground">
                      {o.payableAmount != null ? "客户成交" : "本单收款"}
                    </span>
                    <Price
                      cents={o.payableAmount ?? o.myAmount ?? 0}
                      className="text-[18px] text-brand-600"
                    />
                  </div>

                  {/*
                    🔴 **对账**：钱不经平台，我收下级的钱、再自己把货款结给上级。
                       不摆出「该付上级多少」，商家只能自己按计算器 ——
                       按错就是真金白银的差错。
                  */}
                  {o.myCost != null && o.myCost > 0 && (
                    <>
                      <div className="flex items-baseline justify-between text-[13px]">
                        <span className="text-muted-foreground">该付上级</span>
                        <Price cents={o.myCost} />
                      </div>
                      <div className="flex items-baseline justify-between border-t border-border pt-2.5 text-[13px]">
                        <span className="text-muted-foreground">我赚</span>
                        <Price
                          cents={(o.payableAmount ?? o.myAmount ?? 0) - o.myCost}
                          className="text-brand-600"
                        />
                      </div>
                    </>
                  )}

                  {o.isMine === false && (
                    <p className="text-[12px] text-muted-foreground">下级卖的，货从你这里出</p>
                  )}

                  {/*
                    🔴 线下收款：**只有这一单的收款方**看得到这个按钮，
                       而且服务端还会再校验一次。
                    ⚠️ 只在「我自己卖的 + 还没收款」时出 —— 下级卖的单不该我来确认。
                  */}
                  {/* ⚠️ 已取消的单不出这个按钮 —— 取消只改 fulfillment，payment 仍然是 pending。 */}
                  {o.isMine !== false &&
                    o.payment === "pending" &&
                    o.fulfillment !== "cancelled" &&
                    orgId && (
                    <ConfirmPayBox
                      orderId={o.id}
                      orgId={orgId}
                      amount={o.payableAmount ?? o.myAmount ?? 0}
                      onDone={() => list.refetch()}
                    />
                  )}

                  {/*
                    🔴 只在「卖家自己备货发货」且这单确实待发时才出发货框。
                       供应商直发时给一个填了也发不出去的输入框，是最糟的交互。
                  */}
                  {policy.data?.shipBy === "seller_stock" &&
                    o.payment === "paid" &&
                    o.fulfillment === "pending" &&
                    orgId && (
                      <ShipBox orderId={o.id} orgId={orgId} onDone={() => list.refetch()} />
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
